import type { ResponseStep } from '@mockd/shared/types/rule';
import { stripTemplatesForValidation } from '@mockd/shared/utils';
import { JsonEditor } from '../common/JsonEditor';
import { HeadersEditor } from '../common/HeadersEditor';
import { TemplateVariableRef } from '../common/TemplateVariableRef';
import { StatusBadge } from '../common/StatusBadge';
import { useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon, ChevronUpIcon, XIcon } from 'lucide-react';

interface ResponseStepEditorProps {
  step: ResponseStep;
  index: number;
  total: number;
  onChange: (step: ResponseStep) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

export function ResponseStepEditor({
  step,
  index,
  total,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
}: ResponseStepEditorProps) {
  const [collapsed, setCollapsed] = useState(false);

  const update = <K extends keyof ResponseStep>(key: K, value: ResponseStep[K]) => {
    onChange({ ...step, [key]: value });
  };

  const bodyPreview = step.body.length > 40 ? step.body.slice(0, 40) + '...' : step.body;

  // Validate body is valid JSON (template-aware)
  const bodyValid = (() => {
    if (!step.body.trim()) return false;
    try {
      JSON.parse(stripTemplatesForValidation(step.body));
      return true;
    } catch {
      return false;
    }
  })();

  return (
    <div className="border border-base-300 rounded-box">
      <div
        className="flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-base-200/50"
        onClick={() => setCollapsed(!collapsed)}
      >
        <div className="flex items-center gap-2">
          <ChevronRightIcon className={`size-4 transition-transform ${collapsed ? '' : 'rotate-90'}`} />
          <span className="text-sm font-medium">Step {index + 1}</span>
          <StatusBadge status={step.statusCode} />
          {step.count > 1 && (
            <span className="text-xs text-base-content/70">&times;{step.count}</span>
          )}
          {collapsed && (
            <span className="text-xs text-base-content/70 font-mono truncate max-w-[200px]">
              {bodyPreview}
            </span>
          )}
        </div>

        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          {total > 1 && (
            <>
              <button
                type="button"
                onClick={onMoveUp}
                disabled={index === 0}
                className="btn btn-ghost btn-xs p-0 h-5 min-h-0 disabled:opacity-20"
                title="Move up"
                aria-label="Move step up"
              >
                <ChevronUpIcon className="size-3.5" />
              </button>
              <button
                type="button"
                onClick={onMoveDown}
                disabled={index === total - 1}
                className="btn btn-ghost btn-xs p-0 h-5 min-h-0 disabled:opacity-20"
                title="Move down"
                aria-label="Move step down"
              >
                <ChevronDownIcon className="size-3.5" />
              </button>
            </>
          )}
          <button
            type="button"
            onClick={onRemove}
            className="btn btn-ghost btn-xs btn-square text-error"
            title="Remove step"
            aria-label="Remove step"
          >
            <XIcon className="size-4" />
          </button>
        </div>
      </div>

      {!collapsed && (
        <div className="px-3 pb-3 space-y-3 border-t border-base-200">
          <div className="grid grid-cols-4 gap-3 mt-3">
            <div className="fieldset min-w-0">
              <label className="fieldset-legend">Status Code</label>
              <input
                type="number"
                value={step.statusCode}
                onChange={(e) => update('statusCode', parseInt(e.target.value, 10) || 200)}
                min="100"
                max="599"
                className="input input-sm w-full"
              />
            </div>

            <div className="fieldset min-w-0">
              <label className="fieldset-legend">Repeat Count</label>
              <input
                type="number"
                value={step.count}
                onChange={(e) => update('count', Math.max(1, parseInt(e.target.value, 10) || 1))}
                min="1"
                className="input input-sm w-full"
              />
            </div>

            <div className="fieldset min-w-0">
              <label className="fieldset-legend">Delay (ms)</label>
              <input
                type="number"
                value={step.delayMs || 0}
                onChange={(e) => update('delayMs', parseInt(e.target.value, 10) || 0)}
                min="0"
                className="input input-sm w-full"
              />
            </div>

            <div className="fieldset min-w-0">
              <label className="fieldset-legend">Delay Max (ms)</label>
              <input
                type="number"
                value={step.delayMaxMs || 0}
                onChange={(e) => update('delayMaxMs', parseInt(e.target.value, 10) || 0)}
                min="0"
                className="input input-sm w-full"
              />
            </div>
          </div>

          <div className="fieldset">
            <label className="fieldset-legend">
              Response Body (JSON)
              {!bodyValid && step.body.trim() && (
                <span className="font-normal text-error">Invalid JSON</span>
              )}
            </label>
            <JsonEditor
              value={step.body}
              onChange={(v) => update('body', v)}
              rows={3}
              templateAware
            />
            <TemplateVariableRef />
          </div>

          <div className="fieldset">
            <label className="fieldset-legend">Response Headers</label>
            <HeadersEditor
              value={step.headers && Object.keys(step.headers).length > 0 ? step.headers : null}
              onChange={(h) => update('headers', h || {})}
              placeholder={{ key: 'X-Custom-Header', value: 'value' }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
